import { useBreakpoint } from "@/design-system/salt";

export type SaltBreakpoint = "xs" | "sm" | "md" | "lg" | "xl";

/** Salt's DEFAULT_BREAKPOINTS, restated so the fallback below can answer without a provider. */
const BREAKPOINT_MIN_WIDTH: Record<SaltBreakpoint, number> = {
  xs: 0,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
};

/**
 * True when the viewport is at or above `breakpoint`, using Salt's own scale
 * (xs 0 · sm 600 · md 960 · lg 1280 · xl 1920).
 *
 * Use this instead of a CSS-only gate when a region has a mobile twin: a CSS gate
 * mounts both branches, which double-renders tables and double-fetches their data.
 *
 * This exists because the Tailwind removal deleted the responsive half of ~350
 * layout decisions. Sites written as an inline `display: none` plus a `lg:block`
 * override lost the override and now never render at any width.
 *
 * ── WHY THERE IS A FALLBACK ─────────────────────────────────────────────────────────────────
 * `useBreakpoint()` reads a React context whose default is `{ matchedBreakpoints: [] }`, and the
 * thing that populates it is SaltProvider. Outside one, this returned false for EVERY breakpoint
 * — which does not read as "no provider", it reads as "the viewport is narrower than 600px". So
 * any subtree rendered without SaltProvider silently took the phone branch of every responsive
 * decision. That is exactly how ~12 test files came to assert `role="table"` against a component
 * that had quietly rendered stacked mobile cards instead, and the failure looked like a broken
 * table rather than a missing provider.
 *
 * `main.tsx` mounts SaltFoundationProvider at the root, so in the running app the context is
 * always populated and this fallback never fires. It exists so that "no provider" answers the
 * actual viewport rather than asserting a phone.
 */
export function useIsAtLeast(breakpoint: SaltBreakpoint): boolean {
  const { matchedBreakpoints } = useBreakpoint();

  if (matchedBreakpoints.length > 0) {
    return matchedBreakpoints.includes(breakpoint);
  }

  // No SaltProvider above us. Ask the platform directly rather than reporting a phone.
  // Unsubscribed on purpose: this path has no provider to re-render it on resize, and a wrong
  // answer that never updates is still better than a wrong answer that is always "smallest".
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(`(min-width: ${BREAKPOINT_MIN_WIDTH[breakpoint]}px)`).matches;
}
